import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Footer from './Footer';
import AOS from 'aos';
import 'aos/dist/aos.css';
import jobListings from '../../data/jobListings';
import { useMediaQuery } from 'react-responsive';
import ContactUsForm from './ContactUsForm';

const Careers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [filteredJobs, setFilteredJobs] = useState(jobListings);
  const [showModal, setShowModal] = useState(false);
  
  // Add responsive media query hook
  const isMobile = useMediaQuery({ maxWidth: 767 });
  
  // Initialize AOS animation library
  useEffect(() => {
    // Add CSS to fix select dropdown option text color
    const style = document.createElement('style');
    style.textContent = `
      .department-select option {
        color: #293241 !important; 
        background-color: white !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Initialize AOS animation library
  useEffect(() => {
    AOS.init({
      duration: isMobile ? 800 : 1000,
      once: isMobile,
      mirror: !isMobile,
      disable: 'phone'
    });
  }, [isMobile]);
  
  // Functions to open and close the modal
  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);
  
  // Get unique departments for filter dropdown
  const departments = ['All', ...new Set(jobListings.map(job => job.department))];
  
  // Filter jobs based on search term and department
  useEffect(() => {
    let filtered = jobListings;
    
    if (searchTerm) {
      filtered = filtered.filter(job => 
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedDepartment !== 'All') {
      filtered = filtered.filter(job => job.department === selectedDepartment);
    }
    
    setFilteredJobs(filtered);
  }, [searchTerm, selectedDepartment]);
  
  return (
    <div className="modern-ai-theme">
      {/* Modern grid background with lines */}
      <div className="grid-lines" style={{ zIndex: 0 }}></div>
      
      {/* Hero section */}
      <section className="modern-hero" style={{ paddingTop: '120px', minHeight: 'auto', position: 'relative', zIndex: 1 }}>
        <div className="hero-blob" style={{ zIndex: 0 }}></div>
        <Container>
          <Row className="align-items-center">
            <Col lg={12} className="text-center mb-5">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1>
                  <span className="main-heading">Careers at ARAMA</span>
                </h1>
                <h2 className="mb-4" style={{ 
                  fontSize: isMobile ? '1.6rem' : '2.25rem',
                  lineHeight: isMobile ? '1.3' : '1.4'
                }}>
                  Join Our Mission to <span style={{ color: 'var(--ai-teal)' }}>Transform Pricing</span> for Small Businesses
                </h2>
                <p className="mb-4" style={{ 
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  lineHeight: isMobile ? '1.5' : '1.6',
                  maxWidth: '800px',
                  margin: '0 auto'
                }}>
                  We're building the future where small brands have the same pricing power as retail giants. Let's level the playing field by making AI accessible to everyone, together.
                </p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
      
      {/* Job listings section */}
      <section className="py-5" style={{ background: 'var(--ai-white)', position: 'relative', zIndex: 2 }}>
        <Container>
          <Row className="mb-4">
            <Col lg={6} md={8} className="mx-auto">
              {/* Removed AOS animation from search container for better interactivity */}
              <div className="search-container" style={{ position: 'relative', zIndex: 10 }}>
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Search positions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    background: 'rgba(224, 251, 252, 0.4)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 20px',
                    color: 'var(--ai-light)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    zIndex: 10
                  }}
                />
                <select
                  className="form-select department-select"
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  style={{
                    background: 'rgba(224, 251, 252, 0.4)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 20px',
                    color: 'var(--ai-light)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    zIndex: 10
                  }}
                >
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            </Col>
          </Row>
          
          {filteredJobs.length === 0 ? (
            <Row>
              <Col className="text-center py-5">
                <h3 className="text-muted">No positions found matching your criteria</h3>
                <p>Try changing your search or filter</p>
              </Col>
            </Row>
          ) : (
            <Row>
              {filteredJobs.map((job, index) => (
                <Col lg={6} className="mb-4" key={job.id}>
                  {/* Card without motion wrapper for better clickability */}
                  <Link 
                    to={`/careers/${job.id}`} 
                    style={{ 
                      textDecoration: 'none',
                      display: 'block',
                      position: 'relative',
                      zIndex: 5
                    }}
                    className="job-card-link"
                  >
                    <Card 
                      className="glass-card h-100" 
                      style={{ 
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                        position: 'relative',
                        zIndex: 5
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-10px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <h3 style={{ color: 'var(--ai-teal)' }}>{job.title}</h3>
                          <Badge bg="secondary" style={{ background: 'var(--ai-purple)', padding: '8px 12px' }}>
                            {job.department}
                          </Badge>
                        </div>
                        <div className="mb-3">
                          <Badge bg="light" text="dark" className="me-2" style={{ padding: '5px 10px' }}>
                            <i className="bi bi-geo-alt me-1"></i> {job.location}
                          </Badge>
                          <Badge bg="light" text="dark" style={{ padding: '5px 10px' }}>
                            <i className="bi bi-clock me-1"></i> {job.type}
                          </Badge>
                        </div>
                        <p>{job.description}</p>
                        <div className="text-end mt-3">
                          <span className="text-light">View Details <i className="bi bi-arrow-right"></i></span>
                        </div>
                      </Card.Body>
                    </Card>
                  </Link>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </section>
      
      {/* Work with us section */}
      <section className="py-5" style={{ 
        background: 'var(--ai-gradient-1)', 
        position: 'relative', 
        overflow: 'hidden',
        zIndex: 1
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle at center, rgba(61,90,128,0) 0%, rgba(61,90,128,0.15) 100%)',
          zIndex: 0
        }}></div>
        <Container className="position-relative py-4 py-md-5" style={{ zIndex: 2 }}>
          <Row className="align-items-center">
            <Col lg={8} md={12} className="text-white text-center text-lg-start mb-4 mb-lg-0">
              <h2 className="mb-3" style={{ 
                fontSize: isMobile ? '1.6rem' : '2.25rem',
                lineHeight: isMobile ? '1.3' : '1.4'
              }}>Don't see the right role? We're still growing</h2>
              <p>We're on a mission to kill the guesswork in pricing. If you're passionate about helping small businesses thrive, we want to hear from you.</p>
            </Col>
            <Col lg={4} md={12} className="text-center">
              <button
                onClick={handleOpenModal}
                className="btn btn-light btn-lg"
                style={{ 
                  padding: isMobile ? '12px 20px' : '12px 24px',
                  fontSize: isMobile ? '0.95rem' : '1rem',
                  marginTop: isMobile ? '10px' : '0',
                  touchAction: 'manipulation',
                  minWidth: isMobile ? '180px' : '200px',
                  position: 'relative',
                  zIndex: 5,
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                Contact Us
              </button>
            </Col>
          </Row>
        </Container>
      </section>
      
      {/* Contact Us Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size={isMobile ? "sm" : "lg"}>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="w-100 text-center" style={{ fontSize: isMobile ? '1.4rem' : '1.75rem' }}>
            Get in Touch
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: isMobile ? '15px' : '20px' }}>
          <ContactUsForm />
        </Modal.Body>
      </Modal>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Careers; 